import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  setSearchFilter,
  clearFilters,
  setSearchQuery,
  performSearch,
} from "../../slices/searchSlice";
import { RadioGroup } from "@headlessui/react";
import { FunnelIcon } from "@heroicons/react/24/outline";

const contentTypes = [
  { id: "all", name: "All Content", value: "all" },
  { id: "events", name: "Events Only", value: "events" },
  { id: "resources", name: "Resources Only", value: "resources" },
];

interface SearchFiltersProps {
  onRecentSearchClick?: (search: string) => void;
  isMobile?: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  onRecentSearchClick,
  isMobile = false,
}) => {
  const dispatch = useDispatch();
  const { selectedFilters, searchResults, facets } = useSelector(
    (state: RootState) => state.search,
  );

  // Get dynamic facet values from Algolia, fallback to empty arrays if not available
  const resourceTypes = facets.resourceType
    ? Object.keys(facets.resourceType)
    : [];
  const eventTypes = facets.eventType ? Object.keys(facets.eventType) : [];
  const eventCategories = facets.eventCategories
    ? Object.keys(facets.eventCategories)
    : [];

  const counts: Record<string, number> = {
    all: searchResults.totalEvents + searchResults.totalResources,
    events: searchResults.totalEvents,
    resources: searchResults.totalResources,
  };

  const handleContentTypeChange = (value: string) => {
    dispatch(
      setSearchFilter({ type: value as "all" | "events" | "resources" }),
    );
  };

  const handleResourceTypeChange = (type: string) => {
    const currentType = selectedFilters.resourceType;
    if (currentType === type) {
      // Deselect if clicking the same filter
      dispatch(setSearchFilter({ resourceType: undefined }));
    } else {
      dispatch(setSearchFilter({ resourceType: type }));
    }
  };

  const handleEventTypeChange = (type: string) => {
    const currentType = selectedFilters.eventType;
    if (currentType === type) {
      // Deselect if clicking the same filter
      dispatch(setSearchFilter({ eventType: undefined }));
    } else {
      dispatch(setSearchFilter({ eventType: type }));
    }
  };

  const handleEventCategoryChange = (category: string) => {
    const currentCategory = selectedFilters.eventCategory;
    if (currentCategory === category) {
      // Deselect if clicking the same filter
      dispatch(setSearchFilter({ eventCategory: undefined }));
    } else {
      dispatch(setSearchFilter({ eventCategory: category }));
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const hasActiveFilters =
    selectedFilters.type !== "all" ||
    selectedFilters.resourceType ||
    selectedFilters.eventType ||
    selectedFilters.eventCategory ||
    (selectedFilters.tags && selectedFilters.tags.length > 0) ||
    (selectedFilters.categories && selectedFilters.categories.length > 0);

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-900">Filters</h3>
        </div>
        {hasActiveFilters && (
          <button
            className="text-xs text-indigo-600 hover:text-indigo-500"
            onClick={handleClearFilters}
            type="button"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Content Type Filter */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 mb-2">Content Type</h4>
        <RadioGroup
          onChange={handleContentTypeChange}
          value={selectedFilters.type || "all"}
        >
          <div className="space-y-1">
            {contentTypes.map((type) => (
              <RadioGroup.Option
                className={({ active, checked }) =>
                  `${
                    active
                      ? "border-indigo-600 ring-2 ring-indigo-600"
                      : "border-gray-200"
                  }
                  ${
                    checked
                      ? "bg-indigo-50 border-indigo-600"
                      : "bg-white hover:bg-gray-50"
                  }
                  relative flex cursor-pointer rounded-lg border px-3 py-2 transition-colors`
                }
                key={type.id}
                value={type.value}
              >
                {({ checked }) => (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <RadioGroup.Label
                          as="p"
                          className={`font-medium ${
                            checked ? "text-indigo-900" : "text-gray-900"
                          }`}
                        >
                          {type.name}
                        </RadioGroup.Label>
                      </div>
                    </div>
                    {counts[type.value] > 0 && (
                      <span className="text-xs text-gray-500">
                        {counts[type.value]}
                      </span>
                    )}
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Resource Type Filter - Show only when resources are selected and we have facets */}
      {(selectedFilters.type === "all" ||
        selectedFilters.type === "resources") &&
        resourceTypes.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-gray-700 mb-2">
              Resource Type
            </h4>
            <div className="space-y-1">
              {resourceTypes.map((type) => {
                const count = facets.resourceType?.[type] || 0;
                return (
                  <button
                    className={`w-full flex justify-between items-center px-3 py-1.5 rounded-md text-sm transition-colors ${
                      selectedFilters.resourceType === type
                        ? "bg-indigo-100 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    key={type}
                    onClick={() => handleResourceTypeChange(type)}
                  >
                    <span>{type}</span>
                    <span className="text-xs text-gray-500">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* Event Type Filter - Show only when events are selected and we have facets */}
      {(selectedFilters.type === "all" || selectedFilters.type === "events") &&
        eventTypes.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-gray-700 mb-2">
              Event Type
            </h4>
            <div className="space-y-1">
              {eventTypes.map((type) => {
                const count = facets.eventType?.[type] || 0;
                return (
                  <button
                    className={`w-full flex justify-between items-center px-3 py-1.5 rounded-md text-sm transition-colors ${
                      selectedFilters.eventType === type
                        ? "bg-indigo-100 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    key={type}
                    onClick={() => handleEventTypeChange(type)}
                    type="button"
                  >
                    <span>{type}</span>
                    <span className="text-xs text-gray-500">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* Event Category Filter - Show only when events are selected and we have facets */}
      {(selectedFilters.type === "all" || selectedFilters.type === "events") &&
        eventCategories.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-gray-700 mb-2">
              Event Category
            </h4>
            <div className="space-y-1">
              {eventCategories.map((category) => {
                const count = facets.eventCategories?.[category] || 0;
                return (
                  <button
                    className={`w-full flex justify-between items-center px-3 py-1.5 rounded-md text-sm transition-colors ${
                      selectedFilters.eventCategory === category
                        ? "bg-indigo-100 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    key={category}
                    onClick={() => handleEventCategoryChange(category)}
                    type="button"
                  >
                    <span>{category}</span>
                    <span className="text-xs text-gray-500">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* Recent Searches */}
      <div className="border-t pt-4">
        <h4 className="text-xs font-medium text-gray-700 mb-2">
          Recent Searches
        </h4>
        <div className="space-y-1">
          {useSelector((state: RootState) => state.search.recentSearches)
            .slice(0, 5)
            .map((search, index) => (
              <button
                className="w-full text-left px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 truncate"
                key={index}
                onClick={() => {
                  if (onRecentSearchClick) {
                    // Use callback from parent to update local state and trigger search
                    onRecentSearchClick(search);
                  } else {
                    // Fallback to just updating Redux state
                    dispatch(setSearchQuery(search));
                  }
                }}
              >
                {search}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;
